import type { CreateSessionEventRequest } from "@/shared/domain/events/EventRecord";
import { hasSessionEvents } from "@/shared/domain/orgs";

import { ApprovalMode } from "./approvalModes";
import {
  type ClientToolDeps,
  type ClientToolExecutionResult,
  createClientTool,
} from "./ClientTools";
import { CreateEventsConfirmation } from "./CreateEventsConfirmation";
import {
  commonEventFields,
  createEachEvent,
  EVENT_COMMON_PROPERTIES,
  type EventInputCommon,
} from "./eventCreation";

interface EventTopicInput {
  topic_id: string;
  /** Schema field IDs (`sf_…`) of fields of the topic. */
  field_ids?: string[];
}

interface SessionEventInput extends EventInputCommon {
  /** The session the event belongs to. */
  session_id: string;
  /** The topics, or topic fields, the event is about. */
  topics?: EventTopicInput[];
}

export interface CreateSessionEventsInput {
  events: SessionEventInput[];
}

/**
 * The request that creates `event` on its session.
 *
 * @throws Error when the event names no session, or when the org's event
 * model is known and the org has no session events.
 */
function toCreateSessionEventRequest(
  event: SessionEventInput,
  deps: ClientToolDeps,
): CreateSessionEventRequest {
  if (!event.session_id) {
    throw new Error("session_id is required");
  }
  if (deps.eventModel !== undefined && !hasSessionEvents(deps.eventModel)) {
    throw new Error(
      "This organization has no session events; use client_create_events",
    );
  }
  return {
    ...commonEventFields(event),
    session_id: event.session_id,
    topics: (event.topics ?? []).map((topic) => ({
      topic_id: topic.topic_id,
      field_ids: topic.field_ids,
    })),
  };
}

const inputSchema: Record<string, unknown> = {
  type: "object",
  properties: {
    events: {
      type: "array",
      description: "Array of events to create.",
      items: {
        type: "object",
        properties: {
          ...EVENT_COMMON_PROPERTIES,
          session_id: {
            type: "string",
            description: "The session (se_...) the event belongs to.",
          },
          topics: {
            type: "array",
            description:
              "The topics, or topic fields, the event is about. Each entry names a topic by topic identity ID (ti_...), optionally narrowed to schema field IDs (sf_...) of that topic (omit field_ids for the whole topic; an empty list is refused). A field ID names a field of one schema version only. Omit for an event about all of the session's data.",
            items: {
              type: "object",
              properties: {
                topic_id: { type: "string" },
                field_ids: { type: "array", items: { type: "string" } },
              },
              required: ["topic_id"],
            },
          },
        },
        required: ["name", "start_time", "end_time", "session_id"],
      },
    },
  },
  required: ["events"],
};

async function execute(
  input: CreateSessionEventsInput,
  deps?: ClientToolDeps,
): Promise<ClientToolExecutionResult> {
  return createEachEvent(input.events, deps, toCreateSessionEventRequest);
}

export const tool = createClientTool<CreateSessionEventsInput>({
  name: "client_create_session_events",
  description:
    "Create one or more time-based events on sessions. " +
    "Events mark interesting time ranges (e.g., sensor anomalies, system events, " +
    "behavioral patterns); each belongs to one session and is optionally about " +
    "specific topics or topic fields of it. " +
    "Timestamps are in nanoseconds since Unix epoch.",
  input_schema: inputSchema,
  execute,
  defaultApprovalMode: ApprovalMode.REQUIRE_APPROVAL,
  allowableApprovalModes: [
    ApprovalMode.REQUIRE_APPROVAL,
    ApprovalMode.FOLLOW_AUTO,
    ApprovalMode.AUTO_EXECUTE,
  ],
  displayName: "Create Session Events",
  displayDescription: "Creates time-based events on sessions.",
  ConfirmationContent: CreateEventsConfirmation,
});
